import { useState, useEffect } from 'react'
import { useStore } from '../store/useStore'
import Layout from '../components/Layout'
import { api } from '../lib/api'
import { useAlert } from '../components/AlertProvider'
import { STAGE_3_QUESTIONS } from '../config/profileQuestions'
import type { MyPersona } from '../store/types'

const PERSONALITY_OPTIONS = [
  '친절함', '유머러스', '진지함', '차분함', '활발함',
  '따뜻함', '지적임', '호기심 많음', '배려심 있음', '솔직함'
]

const TOPIC_OPTIONS = [
  '여행', '음악', '영화', '음식', '운동',
  '게임', '독서', '패션', '요리', '사진'
]

const AVOID_TOPIC_OPTIONS = [
  '정치', '종교', '돈', '과거 연애', '가족 문제'
]

export default function MyPersonaPage() {
  const { myPersona, setMyPersona } = useStore()
  const { showAlert, showConfirm } = useAlert()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<Partial<MyPersona>>({
    personality_traits: [],
    conversation_style: { tone: 'friendly', responseLength: 'medium', emoji_usage: 'moderate' },
    preferred_topics: [],
    avoid_topics: [],
    greeting_style: '',
    auto_respond_enabled: false,
    auto_respond_delay: 60,
  })

  // Stage 3 ideal type answers
  const [stage3Answers, setStage3Answers] = useState<Record<string, string>>({})

  useEffect(() => {
    fetchPersona()
    // Load Stage 3 answers from localStorage
    const savedAnswers = localStorage.getItem('stage3_answers')
    if (savedAnswers) {
      try {
        setStage3Answers(JSON.parse(savedAnswers))
      } catch (e) {
        console.error('Failed to parse stage3_answers:', e)
      }
    }
  }, [])

  const fetchPersona = async () => {
    try {
      setLoading(true)
      const data = await api.myPersona.get()
      setMyPersona(data)
      setFormData({
        personality_traits: data.personality_traits || [],
        conversation_style: data.conversation_style || { tone: 'friendly', responseLength: 'medium', emoji_usage: 'moderate' },
        preferred_topics: data.preferred_topics || [],
        avoid_topics: data.avoid_topics || [],
        greeting_style: data.greeting_style || '',
        auto_respond_enabled: data.auto_respond_enabled,
        auto_respond_delay: data.auto_respond_delay,
      })
    } catch (error) {
      console.error('Failed to fetch persona:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const updated = await api.myPersona.update(formData)
      setMyPersona(updated)
      // Also save Stage 3 answers to localStorage
      localStorage.setItem('stage3_answers', JSON.stringify(stage3Answers))
      await showAlert('페르소나 설정이 저장되었습니다!')
    } catch (error) {
      console.error('Failed to save persona:', error)
      await showAlert('저장에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const [learning, setLearning] = useState(false)

  const handleLearn = async () => {
    if (!await showConfirm('과거 대화 내용을 분석하여 페르소나를 업데이트하시겠습니까?')) return

    setLearning(true)
    try {
      const result = await api.myPersona.learn()
      if (result.success) {
        await fetchPersona()
        showAlert('스타일 학습이 완료되었습니다!')
      }
    } catch (error) {
      console.error('Learning failed:', error)
      showAlert('학습 중 오류가 발생했습니다.')
    } finally {
      setLearning(false)
    }
  }

  const toggleTrait = (trait: string) => {
    const traits = formData.personality_traits || []
    if (traits.includes(trait)) {
      setFormData({ ...formData, personality_traits: traits.filter(t => t !== trait) })
    } else if (traits.length < 5) {
      setFormData({ ...formData, personality_traits: [...traits, trait] })
    }
  }

  const toggleTopic = (topic: string, field: 'preferred_topics' | 'avoid_topics') => {
    const topics = formData[field] || []
    if (topics.includes(topic)) {
      setFormData({ ...formData, [field]: topics.filter(t => t !== topic) })
    } else if (topics.length < 5) {
      setFormData({ ...formData, [field]: [...topics, topic] })
    }
  }

  const learningStatusLabel = {
    'not_started': '미설정',
    'learning': '학습 중',
    'ready': '준비 완료'
  }

  const learningStatusColor = {
    'not_started': 'badge-ghost',
    'learning': 'badge-warning',
    'ready': 'badge-success'
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[400px]">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* 헤더 */}
        <div className="card bg-gradient-to-br from-coral-50 to-mint-50">
          <div className="card-body">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-3xl">🤖</span>
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold">내 페르소나</h1>
                <p className="text-base-content/60 text-sm">
                  내가 오프라인일 때 나를 대신해서 대화해주는 AI 분신
                </p>
              </div>
              <div className={`badge ${learningStatusColor[myPersona?.learning_status || 'not_started']}`}>
                {learningStatusLabel[myPersona?.learning_status || 'not_started']}
              </div>
            </div>
          </div>
        </div>


        {/* AI 스타일 분석 */}
        <div className="card bg-base-100 shadow-lg border-l-4 border-primary">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="card-title text-lg">AI 스타일 분석</h2>
                <p className="text-sm text-base-content/60">
                  내 과거 대화 내용을 분석하여 성격과 말투를 자동으로 학습합니다.
                </p>
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleLearn}
                disabled={learning}
              >
                {learning ? <span className="loading loading-spinner loading-xs"></span> : '스타일 학습하기'}
              </button>
            </div>
          </div>
        </div>

        {/* 설명 카드 */}
        <div className="card bg-base-100 shadow-sm border border-base-200">
          <div className="card-body">
            <div className="flex items-start gap-3">
              <span className="text-xl">💡</span>
              <div>
                <h3 className="font-medium mb-1">어떻게 작동하나요?</h3>
                <p className="text-sm text-base-content/70">
                  내 페르소나는 내가 설정한 성격과 말투로 매칭된 상대방에게 자동 응답합니다.
                  상대방은 페르소나가 응답 중임을 알 수 있으며, 내가 온라인이 되면 직접 대화를 이어갈 수 있습니다.
                </p>
                <p className="text-xs text-base-content/50 mt-2">
                  무료 사용자: 하루 1명 자동 응대 / 프리미엄: 무제한
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 자동 응대 토글 */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="card-title text-lg">자동 응대</h2>
                <p className="text-sm text-base-content/60">오프라인일 때 페르소나가 대신 응답</p>
              </div>
              <input
                type="checkbox"
                className="toggle toggle-primary toggle-lg"
                checked={formData.auto_respond_enabled}
                onChange={(e) => setFormData({ ...formData, auto_respond_enabled: e.target.checked })}
              />
            </div>
          </div>
        </div>

        {/* 성격 설정 */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <h2 className="card-title text-lg">페르소나 성격</h2>
            <p className="text-sm text-base-content/60 mb-4">최대 5개 선택</p>
            <div className="flex flex-wrap gap-2">
              {PERSONALITY_OPTIONS.map(trait => (
                <button
                  key={trait}
                  className={`btn btn-sm rounded-full ${formData.personality_traits?.includes(trait)
                    ? 'btn-primary'
                    : 'btn-outline'
                    }`}
                  onClick={() => toggleTrait(trait)}
                >
                  {trait}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 대화 스타일 */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body space-y-4">
            <h2 className="card-title text-lg">대화 스타일</h2>

            {/* 톤 */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">말투</span>
              </label>
              <select
                className="select select-bordered"
                value={formData.conversation_style?.tone || 'friendly'}
                onChange={(e) => setFormData({
                  ...formData,
                  conversation_style: { ...formData.conversation_style, tone: e.target.value }
                })}
              >
                <option value="formal">정중한</option>
                <option value="friendly">친근한</option>
                <option value="casual">편한</option>
                <option value="playful">장난스러운</option>
              </select>
            </div>

            {/* 답변 길이 */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">답변 길이</span>
              </label>
              <select
                className="select select-bordered"
                value={formData.conversation_style?.responseLength || 'medium'}
                onChange={(e) => setFormData({
                  ...formData,
                  conversation_style: {
                    ...formData.conversation_style,
                    responseLength: e.target.value as 'short' | 'medium' | 'long'
                  }
                })}
              >
                <option value="short">짧게 (1-2문장)</option>
                <option value="medium">보통 (3-4문장)</option>
                <option value="long">길게 (5문장 이상)</option>
              </select>
            </div>

            {/* 이모지 사용 */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">이모지 사용</span>
              </label>
              <select
                className="select select-bordered"
                value={formData.conversation_style?.emoji_usage || 'moderate'}
                onChange={(e) => setFormData({
                  ...formData,
                  conversation_style: {
                    ...formData.conversation_style,
                    emoji_usage: e.target.value as 'none' | 'minimal' | 'moderate' | 'frequent'
                  }
                })}
              >
                <option value="none">사용 안 함</option>
                <option value="minimal">가끔</option>
                <option value="moderate">적당히</option>
                <option value="frequent">자주</option>
              </select>
            </div>
          </div>
        </div>

        {/* 선호 주제 */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <h2 className="card-title text-lg">선호하는 대화 주제</h2>
            <p className="text-sm text-base-content/60 mb-4">최대 5개 선택</p>
            <div className="flex flex-wrap gap-2">
              {TOPIC_OPTIONS.map(topic => (
                <button
                  key={topic}
                  className={`btn btn-sm rounded-full ${formData.preferred_topics?.includes(topic)
                    ? 'btn-secondary'
                    : 'btn-outline'
                    }`}
                  onClick={() => toggleTopic(topic, 'preferred_topics')}
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 피해야 할 주제 */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <h2 className="card-title text-lg">피해야 할 주제</h2>
            <p className="text-sm text-base-content/60 mb-4">페르소나가 이 주제는 피합니다</p>
            <div className="flex flex-wrap gap-2">
              {AVOID_TOPIC_OPTIONS.map(topic => (
                <button
                  key={topic}
                  className={`btn btn-sm rounded-full ${formData.avoid_topics?.includes(topic)
                    ? 'btn-error btn-outline'
                    : 'btn-outline'
                    }`}
                  onClick={() => toggleTopic(topic, 'avoid_topics')}
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 첫 인사 스타일 */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <h2 className="card-title text-lg">첫 인사 스타일</h2>
            <p className="text-sm text-base-content/60 mb-4">새로운 매칭에게 보내는 첫 인사 예시</p>
            <textarea
              className="textarea textarea-bordered w-full h-24"
              placeholder="예: 안녕하세요! 프로필 보고 인사드려요 :)"
              value={formData.greeting_style || ''}
              onChange={(e) => setFormData({ ...formData, greeting_style: e.target.value })}
            />
          </div>
        </div>

        {/* Stage 3: 이상형 정보 (AI 참고용) */}
        <div className="card bg-gradient-to-br from-see-50 to-see-100 shadow-xl border-2 border-see-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-see-200/30 rounded-full blur-3xl"></div>
          <div className="card-body relative z-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-see-400 to-see-600 flex items-center justify-center shadow-lg">
                <span className="text-2xl">👁️</span>
              </div>
              <div className="flex-1">
                <h2 className="card-title text-lg bg-gradient-to-r from-see-600 to-see-700 bg-clip-text text-transparent">이상형 정보</h2>
                <p className="text-sm text-base-content/60">AI가 당신의 이상형을 이해하고 더 나은 대화를 도와줍니다</p>
              </div>
            </div>

            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 mt-3 border border-see-200">
              <div className="space-y-4">
                {STAGE_3_QUESTIONS.map((q) => (
                  <div key={q.id} className="form-control">
                    <label className="label">
                      <span className="label-text font-medium text-see-900">{q.text}</span>
                    </label>
                    <input
                      type="text"
                      className="input input-bordered w-full focus:border-see-400 focus:outline-see-400"
                      placeholder={q.placeholder || '답변을 입력해주세요'}
                      value={stage3Answers[q.id] || ''}
                      onChange={(e) => setStage3Answers({ ...stage3Answers, [q.id]: e.target.value })}
                    />
                  </div>
                ))}
              </div>

              <div className="alert bg-see-100/50 mt-4 border-see-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-see-700 shrink-0 h-5 w-5" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span className="text-xs text-see-900">이 정보는 AI 페르소나가 대화할 때 참고하며, Stage 3 진입 시 상대방에게도 공개됩니다.</span>
              </div>
            </div>
          </div>
        </div>

        {/* 저장 버튼 */}
        <div className="sticky bottom-4">
          <button
            className="btn btn-primary btn-lg w-full rounded-full shadow-lg"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <span className="loading loading-spinner loading-sm" />
            ) : (
              '저장하기'
            )}
          </button>
        </div>
      </div >
    </Layout >
  )
}
